import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { SituAnalyticsWebApiClient, WarmCachePlanCompanyResult, WarmCachePlanResult } from 'situanalytics-webapi-client';
import { Role } from 'src/app/shared/models/role.model';

interface CompanyBillingSummaryRow {
  companyId: string;
  companyName: string;
  billableCount: number;
  nonBillableCount: number;
  totalCount: number;
  licenceFee: number;
  rate: number;
  invoicedImpressionsValue: number;
  totalInvoice: number;
  previousMonthBillableImpressions: number | null;
  monthOverMonthChangePercent: number | null;
}

@Component({
  selector: 'monthly-billing-summary',
  templateUrl: './monthly-billing-summary.component.html',
  styleUrls: ['./monthly-billing-summary.component.scss']
})
export class MonthlyBillingSummaryComponent extends SituBaseComponent implements OnInit, OnDestroy {
  loading = false;
  billingSummaries: CompanyBillingSummaryRow[] = [];
  selectedMonth: Date = new Date();
  minDate = new Date(2020, 0, 1); // Jan 1, 2020
  maxDate = new Date(); // Will be set to previous month in constructor

  // Response-level totals from the API
  grandTotalInvoice: number = 0;
  previousMonthGrandTotalBillable: number | null = null;
  grandTotalMonthOverMonthPercent: number | null = null;

  // Impressions cache coverage for the month (GetCachePlan). The billing figures come from the
  // cache, so a company missing days is under-counted until the warmer fills them.
  cachePlan: WarmCachePlanResult | null = null;
  cacheLoadFailed = false;
  private cacheByCompany = new Map<string, WarmCachePlanCompanyResult>();

  displayedColumns: string[] = [
    'company', 'licenceFee', 'billable', 'rate', 'invoicedImpressionsValue',
    'totalInvoice', 'previousMonthBillable', 'variance', 'daysCached'
  ];
  
  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private snackBar: MatSnackBar,
    private situAnalyticsClient: SituAnalyticsWebApiClient
  ) {
    super(authService, stateService);
    
    // Allow up to the current month, shown month-to-date, so cache health can be checked before it closes
    const now = new Date();
    this.maxDate = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
    
    // Initialize to previous month
    this.initializeToPreviousMonth();
  }

  ngOnInit() {
    super.ngOnInit();
    
    // Check if user has Situ admin access
    if (!this.isSituAdmin()) {
      this.router.navigateByUrl('/unauthorized');
      return;
    }
    
    this.loadMonthlyBillingSummary();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  private initializeToPreviousMonth() {
    const now = new Date();
    this.selectedMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    
    // Ensure we don't go before minDate
    if (this.selectedMonth < this.minDate) {
      this.selectedMonth = new Date(this.minDate);
    }
  }

  private isSituAdmin(): boolean {
    return this.authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper]);
  }

  async loadMonthlyBillingSummary() {
    this.loading = true;
    this.billingSummaries = [];

    // Separate call: if it fails the billing still shows, just without coverage.
    this.loadCachePlan();

    try {
      // Call the new GetMonthlyBillingSummary endpoint
      const month = this.selectedMonth.getMonth() + 1; // 1-based month
      const year = this.selectedMonth.getFullYear();

      // Call the endpoint
      const response = await (this.situAnalyticsClient as any).getMonthlyBillingSummary({
        month: month,
        year: year
      });
      
      // Store response-level totals
      this.grandTotalInvoice = response?.grandTotalInvoice || 0;
      this.previousMonthGrandTotalBillable = response?.previousMonthGrandTotalBillableImpressions ?? null;
      this.grandTotalMonthOverMonthPercent = response?.monthOverMonthChangePercent ?? null;

      // Extract company summaries from the response
      const summaries = response?.companySummaries || [];

      if (summaries && Array.isArray(summaries)) {
        this.billingSummaries = summaries.map((item: any) => ({
          companyId: item.companyId,
          companyName: item.companyName || item.companyIdentifier || 'Unknown Company',
          billableCount: item.billableImpressions || 0,
          nonBillableCount: item.nonBillableImpressions || 0,
          totalCount: item.totalImpressions || 0,
          licenceFee: item.licenceFee || 0,
          rate: item.rate || 0,
          invoicedImpressionsValue: item.invoicedImpressionsValue || 0,
          totalInvoice: item.totalInvoice || 0,
          previousMonthBillableImpressions: item.previousMonthBillableImpressions ?? null,
          monthOverMonthChangePercent: item.monthOverMonthChangePercent ?? null
        }));

        // Sort by company name alphabetically
        this.billingSummaries.sort((a, b) => a.companyName.localeCompare(b.companyName));
      } else {
        this.showMessage('No billing data available for the selected month');
      }
      
    } catch (error) {
      this.showMessage('Error loading billing summary data');
    } finally {
      this.loading = false;
    }
  }

  private async loadCachePlan() {
    const month = this.selectedMonth.getMonth() + 1;
    const year = this.selectedMonth.getFullYear();

    this.cachePlan = null;
    this.cacheLoadFailed = false;
    this.cacheByCompany.clear();

    try {
      const plan = await this.situAnalyticsClient.getCachePlan(year, month);

      // Ignore a late response for a month the user has already navigated away from.
      if (this.selectedMonth.getFullYear() !== year || this.selectedMonth.getMonth() + 1 !== month) {
        return;
      }

      this.cachePlan = plan;
      (plan?.companies || []).forEach(c => {
        if (c.companyId) {
          this.cacheByCompany.set(c.companyId.toLowerCase(), c);
        }
      });
    } catch (error) {
      this.cacheLoadFailed = true;
    }
  }

  // Days in the selected month. GetCachePlan only counts up to yesterday (UTC), so its totalDays
  // falls short of this until the warmer has run for the month's last day.
  getDaysInMonth(): number {
    return new Date(this.selectedMonth.getFullYear(), this.selectedMonth.getMonth() + 1, 0).getDate();
  }

  // Impressions months are UTC. The UTC month in progress (or, in Australia on the morning of the
  // 1st, the local month UTC hasn't reached yet) is still filling in: its figures are month-to-date.
  isMonthInProgress(): boolean {
    const now = new Date();
    const selected = this.selectedMonth.getFullYear() * 12 + this.selectedMonth.getMonth();
    return selected >= now.getUTCFullYear() * 12 + now.getUTCMonth();
  }

  // Days every company should have cached: through yesterday for the month in progress, otherwise
  // the whole month.
  getExpectedCachedDays(): number {
    return this.isMonthInProgress()
      ? Math.max(this.cachePlan?.totalDays ?? 0, 0)
      : this.getDaysInMonth();
  }

  getCacheCoverage(row: CompanyBillingSummaryRow): WarmCachePlanCompanyResult | null {
    return row.companyId ? this.cacheByCompany.get(row.companyId.toLowerCase()) ?? null : null;
  }

  isCompanyFullyCached(row: CompanyBillingSummaryRow): boolean {
    return (this.getCacheCoverage(row)?.daysCached ?? 0) >= this.getExpectedCachedDays();
  }

  getIncompleteCacheCompanies(): WarmCachePlanCompanyResult[] {
    const expected = this.getExpectedCachedDays();
    return (this.cachePlan?.companies || []).filter(c => (c.daysCached ?? 0) < expected);
  }

  // Nothing to check yet: the 1st of the month, before any day of it has been warmed.
  hasNoDaysToCheck(): boolean {
    return !!this.cachePlan && this.getExpectedCachedDays() === 0;
  }

  isMonthFullyCached(): boolean {
    return !!this.cachePlan
      && !this.hasNoDaysToCheck()
      && (this.cachePlan.companies?.length ?? 0) > 0
      && this.getIncompleteCacheCompanies().length === 0;
  }

  getIncompleteCacheTooltip(): string {
    return this.getIncompleteCacheCompanies()
      .map(c => `${c.companyName || c.companyId}: ${c.daysCached ?? 0}/${this.getExpectedCachedDays()}`)
      .join('\n');
  }

  onMonthChange(date: Date) {
    this.selectedMonth = date;
    this.loadMonthlyBillingSummary();
  }

  navigateToPreviousMonth() {
    if (this.isPreviousMonthDisabled()) {
      return;
    }
    
    const prevMonth = new Date(
      this.selectedMonth.getFullYear(),
      this.selectedMonth.getMonth() - 1,
      1
    );
    
    this.selectedMonth = prevMonth;
    this.loadMonthlyBillingSummary();
  }

  navigateToNextMonth() {
    if (this.isNextMonthDisabled()) {
      return;
    }
    
    const nextMonth = new Date(
      this.selectedMonth.getFullYear(),
      this.selectedMonth.getMonth() + 1,
      1
    );
    
    this.selectedMonth = nextMonth;
    this.loadMonthlyBillingSummary();
  }

  isPreviousMonthDisabled(): boolean {
    const prevMonth = new Date(
      this.selectedMonth.getFullYear(),
      this.selectedMonth.getMonth() - 1,
      1
    );
    
    return prevMonth < this.minDate;
  }

  isNextMonthDisabled(): boolean {
    const nextMonth = new Date(
      this.selectedMonth.getFullYear(),
      this.selectedMonth.getMonth() + 1,
      1
    );
    
    // Compare with maxDate (which is set to last day of previous month)
    // We want to disable if next month would be after the previous month
    return nextMonth > this.maxDate;
  }

  exportToCsv() {
    const csvData = this.generateCsvData();
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const fileName = `monthly_billing_summary_${this.getMonthYearDisplay()}_${timestamp}.csv`;

    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  private generateCsvData(): string {
    let csv = 'Company Name,Company ID,Licence Fee,Billable Impressions,Rate,Invoiced Impressions Value,Total Invoice,Previous Month Billable,Variance,Variance %\r\n';

    this.billingSummaries.forEach(item => {
      const variance = this.getVariance(item);
      const variancePct = item.monthOverMonthChangePercent != null ? item.monthOverMonthChangePercent.toFixed(2) + '%' : '';
      csv += `"${item.companyName}",${item.companyId},${item.licenceFee},${item.billableCount},${item.rate},${item.invoicedImpressionsValue},${item.totalInvoice},${item.previousMonthBillableImpressions ?? ''},${variance ?? ''},${variancePct}\r\n`;
    });

    // Add totals row
    const totalBillable = this.getTotalBillable();
    const totalInvoice = this.getTotalInvoice();
    csv += `"TOTALS","","",${totalBillable},"","",${totalInvoice},${this.previousMonthGrandTotalBillable ?? ''},"",""\r\n`;

    return csv;
  }

  getMonthYearDisplay(): string {
    return this.selectedMonth.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  }

  getTotalCompanies(): number {
    return this.billingSummaries.length;
  }

  getTotalBillable(): number {
    return this.billingSummaries.reduce((sum, c) => sum + c.billableCount, 0);
  }

  getTotalNonBillable(): number {
    return this.billingSummaries.reduce((sum, c) => sum + c.nonBillableCount, 0);
  }

  getGrandTotal(): number {
    return this.billingSummaries.reduce((sum, c) => sum + c.totalCount, 0);
  }

  getTotalInvoice(): number {
    return this.billingSummaries.reduce((sum, c) => sum + c.totalInvoice, 0);
  }

  getVariance(row: CompanyBillingSummaryRow): number | null {
    if (row.previousMonthBillableImpressions == null) {
      return null;
    }
    return row.billableCount - row.previousMonthBillableImpressions;
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 3000 });
  }

  openCompanyGraph(companyId: string) {
    // Get the month and year for the URL parameters
    const month = this.selectedMonth.getMonth() + 1;
    const year = this.selectedMonth.getFullYear();
    
    // Format month parameter as YYYY-MM (e.g., "2025-06")
    const monthParam = `${year}-${month.toString().padStart(2, '0')}`;
    
    // Build the URL with query parameters for deep linking
    const url = `/reports/product-impressions-graph?companyId=${companyId}&month=${monthParam}`;
    
    // Open in new tab
    window.open(url, '_blank');
  }
}